import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["canvas", "blobInput", "checkbox", "submitBtn"]
  static values = { directUploadUrl: String }

  connect() {
    this.setupCanvas()
    this.isDrawing = false
    this.hasSignature = false
    this.lastX = 0
    this.lastY = 0

    // Find submit button - may be outside controller scope (in bottom bar)
    this.externalSubmitBtn = document.querySelector('[data-signature-pad-target="submitBtn"]')
    this.updateSubmitButton()
  }

  disconnect() {
    // Clean up event listeners to prevent memory leaks
    if (this.canvasTarget && this.boundHandlers) {
      const canvas = this.canvasTarget
      canvas.removeEventListener('mousedown', this.boundHandlers.startDrawing)
      canvas.removeEventListener('mousemove', this.boundHandlers.draw)
      canvas.removeEventListener('mouseup', this.boundHandlers.stopDrawing)
      canvas.removeEventListener('mouseleave', this.boundHandlers.stopDrawing)
      canvas.removeEventListener('touchstart', this.boundHandlers.startDrawing)
      canvas.removeEventListener('touchmove', this.boundHandlers.draw)
      canvas.removeEventListener('touchend', this.boundHandlers.stopDrawing)
      canvas.removeEventListener('touchcancel', this.boundHandlers.stopDrawing)
    }
  }

  setupCanvas() {
    const canvas = this.canvasTarget
    const rect = canvas.getBoundingClientRect()

    // Set canvas internal resolution to match display size for crisp lines
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr

    this.ctx = canvas.getContext('2d')
    this.ctx.scale(dpr, dpr)

    // Drawing styles
    this.ctx.strokeStyle = '#000'
    this.ctx.lineWidth = 2
    this.ctx.lineCap = 'round'
    this.ctx.lineJoin = 'round'

    // White background
    this.ctx.fillStyle = '#fff'
    this.ctx.fillRect(0, 0, rect.width, rect.height)

    // Store display dimensions for coordinate calculations
    this.displayWidth = rect.width
    this.displayHeight = rect.height

    // Bind event listeners
    this.bindEvents()
  }

  bindEvents() {
    const canvas = this.canvasTarget

    // Store bound handlers for cleanup in disconnect()
    this.boundHandlers = {
      startDrawing: (e) => this.startDrawing(e),
      draw: (e) => this.draw(e),
      stopDrawing: () => this.stopDrawing()
    }

    // Mouse events
    canvas.addEventListener('mousedown', this.boundHandlers.startDrawing)
    canvas.addEventListener('mousemove', this.boundHandlers.draw)
    canvas.addEventListener('mouseup', this.boundHandlers.stopDrawing)
    canvas.addEventListener('mouseleave', this.boundHandlers.stopDrawing)

    // Touch events
    canvas.addEventListener('touchstart', this.boundHandlers.startDrawing, { passive: false })
    canvas.addEventListener('touchmove', this.boundHandlers.draw, { passive: false })
    canvas.addEventListener('touchend', this.boundHandlers.stopDrawing)
    canvas.addEventListener('touchcancel', this.boundHandlers.stopDrawing)
  }

  getCoordinates(e) {
    const canvas = this.canvasTarget
    const rect = canvas.getBoundingClientRect()

    let clientX, clientY
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    }
  }

  startDrawing(e) {
    e.preventDefault()
    this.isDrawing = true
    const coords = this.getCoordinates(e)
    this.lastX = coords.x
    this.lastY = coords.y
  }

  draw(e) {
    if (!this.isDrawing) return
    e.preventDefault()

    const coords = this.getCoordinates(e)

    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(coords.x, coords.y)
    this.ctx.stroke()

    this.lastX = coords.x
    this.lastY = coords.y
    this.hasSignature = true
  }

  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.updateSubmitButton()
    }
  }

  clear() {
    // Clear and reset white background
    this.ctx.fillStyle = '#fff'
    this.ctx.fillRect(0, 0, this.displayWidth, this.displayHeight)
    this.hasSignature = false
    this.blobInputTarget.value = ''
    this.updateSubmitButton()
  }

  updateSubmitButton() {
    // Get submit button - could be inside controller scope or external
    const submitBtn = this.hasSubmitBtnTarget ? this.submitBtnTarget : this.externalSubmitBtn
    if (!submitBtn) return

    const canSubmit = this.hasSignature && this.checkboxTarget.checked
    submitBtn.disabled = !canSubmit

    if (canSubmit) {
      submitBtn.classList.remove('opacity-50', 'cursor-not-allowed')
    } else {
      submitBtn.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  checkboxChanged() {
    this.updateSubmitButton()
  }

  // Convert canvas to blob and upload via ActiveStorage DirectUpload
  async uploadSignature() {
    return new Promise((resolve, reject) => {
      this.canvasTarget.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create signature blob'))
          return
        }

        const file = new File([blob], "signature.png", { type: "image/png" })
        const upload = new DirectUpload(file, this.directUploadUrlValue)

        upload.create((error, blob) => {
          if (error) {
            reject(error)
          } else {
            this.blobInputTarget.value = blob.signed_id
            resolve(blob)
          }
        })
      }, 'image/png')
    })
  }

  // Called on form submit - upload signature first
  async submit(event) {
    event.preventDefault()

    if (!this.hasSignature || !this.checkboxTarget.checked) {
      return
    }

    const form = event.target.closest('form') || document.getElementById('agreement-form')
    const submitBtn = this.hasSubmitBtnTarget ? this.submitBtnTarget : this.externalSubmitBtn
    const originalText = submitBtn?.textContent

    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.innerHTML = `
        <svg class="animate-spin size-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Saving...
      `
    }

    try {
      await this.uploadSignature()
      form.submit()
    } catch (error) {
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.textContent = originalText
      }
      if (window.showToast) {
        window.showToast('Failed to save signature. Please try again.', 'error')
      } else {
        alert('Failed to save signature. Please try again.')
      }
    }
  }
}
